import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkFormPage from '../../../components/work/WorkFormPage';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkSelectWithManage from '../../../components/work/WorkSelectWithManage';
import {
  projectTemplatesApi,
  projectCategoriesApi,
  projectSubcategoriesApi,
} from '../../../utils/workApi';
import { show422Error } from '../../../utils/api';
import { buildProjectTemplatePayload } from '../../../utils/workPayloadBuilders';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { Button, Card, Form, Input, Select, Row, Col, message, Spin } from 'antd';
import WorkCheckbox from '../../../components/work/WorkCheckbox';
import { ProjectOutlined, SaveOutlined } from '@ant-design/icons';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import {
  mergeBreadcrumbIntoNavigationState,
  trailWithoutLastCrumb,
} from '../../../utils/breadcrumbTrail';

const { TextArea } = Input;
const { Option } = Select;
const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const ProjectTemplateForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/projects/templates', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const projectNameWatch = Form.useWatch('project_name', form);
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/projects/templates',
    listLabel: 'قالب‌های پروژه',
    entityLabel: isEdit
      ? projectNameWatch || `ویرایش قالب #${id}`
      : 'قالب پروژه جدید',
  });
  const goToTemplateView = (templateId) => {
    navigate(`/work/projects/templates/${templateId}`, {
      state: {
        returnTo: returnToRef.current || '/work/projects/templates',
        ...buildGoBackState(),
        ...mergeBreadcrumbIntoNavigationState({}, trailWithoutLastCrumb(trail)),
      },
    });
  };
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [catModal, setCatModal] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [catModalOpening, setCatModalOpening] = useState(false);
  const [subModalOpening, setSubModalOpening] = useState(false);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const selectedCategory = Form.useWatch('category_id', form);

  const loadCategories = useCallback(async () => {
    const res = await projectCategoriesApi.getCreateData();
    const list = res?.categories || [];
    setCategories(list);
    return list;
  }, []);

  const loadSubcategories = useCallback(async (categoryId, { showLoading = false } = {}) => {
    if (!categoryId) {
      setSubcategories([]);
      return [];
    }
    if (showLoading) setSubcategoriesLoading(true);
    try {
      const res = await projectSubcategoriesApi.getByCategory(categoryId);
      const list = res?.sub_categories || res?.subcategories || res?.data || [];
      setSubcategories(list);
      return list;
    } finally {
      if (showLoading) setSubcategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = isEdit
          ? await projectTemplatesApi.getEditData(id)
          : await projectTemplatesApi.getCreateData();
        const cats = res?.categories || [];
        setCategories(cats);
        if (isEdit && res?.template) {
          const t = res.template;
          form.setFieldsValue({
            project_name: t.project_name,
            category_id: t.category_id,
            sub_category_id: t.sub_category_id,
            project_summary: t.project_summary,
            notes: t.notes,
            manual_timelog: t.manual_timelog === 'enable' || t.manual_timelog === 'on',
          });
          if (t.category_id) await loadSubcategories(t.category_id);
        }
        const subs = res?.sub_categories || res?.subcategories;
        if (subs?.length) setSubcategories(subs);
      } catch {
        message.error('خطا در بارگذاری');
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, form, loadSubcategories]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const body = buildProjectTemplatePayload(values);
      if (isEdit) {
        await projectTemplatesApi.update(id, body);
        goToTemplateView(id);
      } else {
        const res = await projectTemplatesApi.create(body);
        const newId = res?.template?.id;
        if (newId) goToTemplateView(newId);
        else goBackToList();
      }
      message.success('ذخیره شد');
    } catch (e) {
      show422Error(e, form);
    } finally {
      setSubmitting(false);
    }
  };

  const openCatModal = async () => {
    setCatModalOpening(true);
    try {
      await loadCategories();
      setCatModal(true);
    } finally {
      setCatModalOpening(false);
    }
  };

  const openSubModal = async () => {
    if (!selectedCategory) {
      message.warning('ابتدا دسته را انتخاب کنید');
      return;
    }
    setSubModalOpening(true);
    try {
      await loadSubcategories(selectedCategory);
      setSubModal(true);
    } finally {
      setSubModalOpening(false);
    }
  };

  return (
    <WorkFormPage
      title={isEdit ? 'ویرایش الگوی پروژه' : 'الگوی پروژه جدید'}
      subtitle="اطلاعات قالب پروژه"
      icon={ProjectOutlined}
      iconClassName="bg-sky-50 dark:bg-sky-900/20"
      iconColorClass="text-sky-600"
      backPath="/work/projects/templates"
      onBack={goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
    >
      {fetching ? (
        <FormSkeleton rows={6} columns={2} />
      ) : (
        <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
          <Form
            form={form}
            layout="vertical"
            className="work-panel-form"
            onFinish={onFinish}
            onFinishFailed={() => scrollToFirstError(form)}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          >
            <Form.Item
              name="project_name"
              label={label('نام پروژه')}
              rules={[{ required: true, message: 'نام الزامی است' }]}
            >
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <WorkSelectWithManage
                  label="دسته‌بندی"
                  name="category_id"
                  onManageClick={openCatModal}
                  manageLoading={catModalOpening}
                  selectProps={{
                    onChange: (v) => {
                      form.setFieldValue('sub_category_id', undefined);
                      loadSubcategories(v, { showLoading: true });
                    },
                  }}
                >
                  {categories.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.category_name}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Col>
              <Col xs={24} md={12}>
                <Spin spinning={subcategoriesLoading} tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری زیردسته...</span>}>
                  <WorkSelectWithManage
                    label="زیردسته"
                    name="sub_category_id"
                    onManageClick={openSubModal}
                    manageLoading={subModalOpening}
                    selectProps={{
                      allowClear: true,
                      disabled: !selectedCategory || subcategoriesLoading,
                      loading: subcategoriesLoading,
                      placeholder: selectedCategory ? 'انتخاب زیردسته' : 'ابتدا دسته را انتخاب کنید',
                    }}
                  >
                    {subcategories.map((s) => (
                      <Option key={s.id} value={s.id}>
                        {s.category_name}
                      </Option>
                    ))}
                  </WorkSelectWithManage>
                </Spin>
              </Col>
            </Row>
            <Form.Item name="project_summary" label={label('خلاصه')}>
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item name="notes" label={label('یادداشت')}>
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item name="manual_timelog" valuePropName="checked">
              <WorkCheckbox>ثبت دستی زمان</WorkCheckbox>
            </Form.Item>
            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={goBackToList}>انصراف</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<SaveOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
              >
                ذخیره
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <WorkManageEntityModal
        open={catModal}
        onClose={() => setCatModal(false)}
        title="دسته پروژه"
        nameField="category_name"
        loadItems={loadCategories}
        onCreate={(name) => projectCategoriesApi.create(name)}
        onUpdate={(cid, name) => projectCategoriesApi.update(cid, name)}
        onDelete={(cid) => projectCategoriesApi.delete(cid)}
        buildCreatePayload={(v) => v.category_name}
        buildUpdatePayload={(cid, name) => name}
        getItemLabel={(i) => i.category_name}
      />
      <WorkManageEntityModal
        open={subModal}
        onClose={() => setSubModal(false)}
        title="زیردسته"
        nameField="category_name"
        loadItems={() => loadSubcategories(selectedCategory)}
        onCreate={(payload) => projectSubcategoriesApi.create(payload)}
        onUpdate={(sid, name) => projectSubcategoriesApi.update(sid, { category_name: name })}
        onDelete={(sid) => projectSubcategoriesApi.delete(sid)}
        buildCreatePayload={(v) => ({ category_id: selectedCategory, category_name: v.category_name })}
        buildUpdatePayload={(sid, name) => ({ category_name: name })}
        getItemLabel={(i) => i.category_name}
      />
    </WorkFormPage>
  );
};

export default ProjectTemplateForm;
